package com.arena_web.core.viewmodel

import com.arena_web.data.dto.PersonDto
import com.arena_web.domain.repositories.PeopleRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class PeopleUiState(
    /** Cards still to swipe — front of the queue is the current/top card. */
    val queue: List<PersonDto> = emptyList(),
    /** Server says there's potentially more after we exhaust [queue]. */
    val hasMore: Boolean = true,
    val isLoading: Boolean = false,
    /** Set while a swipe action is in flight — disables Like/Skip buttons. */
    val isSwiping: Boolean = false,
    val error: String? = null,
    /**
     * Set the moment a LIKE lands on a person who had previously LIKEd the
     * caller — drives the "It's Match" popup. Cleared via [PeopleViewModel.dismissMatch].
     */
    val matchedPerson: PersonDto? = null,
)

/**
 * Loads the discovery feed lazily and exposes a head-of-queue current card
 * plus optimistic Like/Skip handlers. Pops the top card immediately on click
 * so the UI feels snappy; the actual swipe POST runs in the background, and
 * any failure surfaces as `state.error` (we still keep the card popped — UX
 * preference: don't unpop a card the user already moved on from).
 */
class PeopleViewModel(
    private val peopleRepository: PeopleRepository,
    /**
     * When true, the VM is driving the unauthenticated guest view:
     *  - card feed hits `/people/public` instead of `/people`
     *  - `/people/interactions` is skipped (auth-only)
     *  - swipes are no-ops (the UI gates them and shows AccountRequiredModal)
     */
    private val isGuest: Boolean = false,
) : BaseViewModel() {
    private val _uiState = MutableStateFlow(PeopleUiState())
    val uiState: StateFlow<PeopleUiState> = _uiState.asStateFlow()

    private var nextOffset: Long = 0

    /**
     * Snapshot of user ids whose LIKE on the caller hasn't been responded to —
     * i.e. a LIKE by the caller on any id in this set creates a mutual match.
     * Seeded from `/people/interactions` on init and kept in sync after each
     * swipe (ids that became matches are dropped locally without refetching).
     */
    private var incomingLikerIds: Set<String> = emptySet()

    init {
        loadMore()
        if (!isGuest) loadIncomingLikes()
    }

    /** Top of the queue — what the card stack currently renders front-and-center. */
    fun currentCard(): PersonDto? = _uiState.value.queue.firstOrNull()

    fun like() = recordSwipe(SWIPE_LIKE)
    fun skip() = recordSwipe(SWIPE_SKIP)

    /** Clear the "It's Match" popup — called by both CTAs and the backdrop tap. */
    fun dismissMatch() {
        _uiState.update { it.copy(matchedPerson = null) }
    }

    /**
     * Inject a person at the front of the swipe queue — used when the user
     * taps a "New likes" card in the header Interactions popup. Any existing
     * entry with the same `userId` is removed first so the queue stays
     * deduped, then the person is prepended so they show up as the current
     * card on the next render.
     */
    fun focusPerson(person: PersonDto) {
        _uiState.update {
            val deduped = it.queue.filter { existing -> existing.userId != person.userId }
            it.copy(queue = listOf(person) + deduped)
        }
    }

    private fun loadIncomingLikes() {
        viewModelScope.launch {
            peopleRepository.getInteractions()
                .onSuccess { interactions ->
                    incomingLikerIds = interactions.newLikes.map { it.userId }.toSet()
                }
        }
    }

    private fun recordSwipe(action: String) {
        val current = currentCard() ?: return
        if (_uiState.value.isSwiping) return

        // Mutual-match detection — if the caller is LIKE-ing a person who
        // already LIKEd them, the popup fires immediately (no extra HTTP):
        // the local snapshot of incoming likers is authoritative for this
        // session and gets pruned right after a match.
        val matched = action == SWIPE_LIKE && current.userId in incomingLikerIds
        if (matched) {
            incomingLikerIds = incomingLikerIds - current.userId
        }

        // Optimistic pop — feels instant; backend call runs in background.
        _uiState.update {
            it.copy(
                queue = it.queue.drop(1),
                isSwiping = true,
                error = null,
                matchedPerson = if (matched) current else it.matchedPerson,
            )
        }

        viewModelScope.launch {
            peopleRepository
                .swipe(current.userId, action)
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message ?: "Swipe failed") }
                }
            _uiState.update { it.copy(isSwiping = false) }

            // Prefetch when queue runs low to avoid a "thinking" gap.
            if (_uiState.value.queue.size <= LOW_WATER_MARK && _uiState.value.hasMore) {
                loadMore()
            }
        }
    }

    fun loadMore() {
        if (_uiState.value.isLoading) return
        _uiState.update { it.copy(isLoading = true, error = null) }
        viewModelScope.launch {
            val fetch = if (isGuest) {
                peopleRepository.getPublicFeed(size = PAGE_SIZE, offset = nextOffset)
            } else {
                peopleRepository.getFeed(size = PAGE_SIZE, offset = nextOffset)
            }
            fetch
                .onSuccess { page ->
                    nextOffset += page.people.size
                    _uiState.update {
                        it.copy(
                            queue = it.queue + page.people,
                            hasMore = page.hasMore,
                            isLoading = false,
                        )
                    }
                }
                .onFailure { e ->
                    // Reset hasMore so the empty state stops claiming "more
                    // are on the way" — the EmptyState branch uses both flags
                    // to render either a stuck spinner or a refreshable
                    // "all caught up / something went wrong" panel.
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            hasMore = false,
                            error = e.message ?: "Failed to load people",
                        )
                    }
                }
        }
    }

    companion object {
        // Cards are shown one-at-a-time, so we prefetch in small batches and
        // top up early via [LOW_WATER_MARK] — keeps the round-trip overhead
        // out of the user's swipe rhythm without holding a long queue in memory.
        private const val PAGE_SIZE = 5
        private const val LOW_WATER_MARK = 2
        private const val SWIPE_LIKE = "LIKE"
        private const val SWIPE_SKIP = "SKIP"
    }
}
